import { useMemo } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import { businessesCollection, subscriptionPlansCollection, eq } from "@cliqsuite/db";
import { useAuth } from "./auth";

/** The current business's installed_modules as a Set, live from core.businesses. */
export function useInstalledModules(): { modules: Set<string>; isLoading: boolean } {
  const { user } = useAuth();
  const { data: businesses, isLoading } = useLiveQuery(
    (q) =>
      user
        ? q.from({ businesses: businessesCollection }).where(({ businesses }) => eq(businesses.id, user.business_id))
        : q.from({ businesses: businessesCollection }).where(({ businesses }) => eq(businesses.id, "__none__")),
    [user?.business_id],
  );
  const modules = useMemo(() => new Set((businesses?.[0]?.installed_modules ?? []).filter((m) => m !== "")), [businesses]);
  return { modules, isLoading };
}

/**
 * Whether the business's plan includes the Guest Portal. It is switched per store
 * (core.stores.guest_portal_enabled), and sync-api refuses to switch it on without the plan.
 */
export function useGuestPortalPlan(): { entitled: boolean; planName: string | null } {
  const { user } = useAuth();
  const { data: businesses } = useLiveQuery((q) => q.from({ b: businessesCollection }).where(({ b }) => eq(b.id, user?.business_id ?? "__none__")), [user?.business_id]);
  const { data: plans } = useLiveQuery((q) => q.from({ p: subscriptionPlansCollection }));
  const plan = plans?.find((p) => p.id === businesses?.[0]?.subscription_plan_id);
  const entitled = ((plan?.features as { modules?: string[] } | undefined)?.modules ?? []).includes("guest_portal");
  return { entitled, planName: plan?.name ?? null };
}
